import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';

/* ─── Design tokens — matches Home.module.scss ─── */
const T = {
  bg:        '#f4f3ef',
  surface:   'rgba(255, 255, 255, 0.88)',
  border:    'rgba(26, 48, 80, 0.11)',
  borderMd:  'rgba(26, 48, 80, 0.2)',
  accent:    '#1a3050',
  steel:     '#457b9d',
  ink:       '#1a1c22',
  muted:     '#7a8499',
  mono:      "'IBM Plex Mono', monospace",
  display:   "'Cormorant Garamond', Georgia, serif",
};

/* ─── Nav link data — human-readable labels ─── */
const links = [
  { label: 'Home',      to: '/',          key: 'home' },
  { label: 'About',     to: '/about',     key: 'about' },
  { label: 'Portfolio', to: '/portfolio', key: 'portfolio' },
];

/* ─── Desktop nav link ─── */
function NavItem({ link, isActive }) {
  const [hovered, setHovered] = useState(false);
  const lit = isActive || hovered;

  return (
    <Link
      to={link.to}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      style={{ textDecoration: 'none' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 0',
        fontFamily: T.mono,
        fontSize: 11,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: isActive ? T.accent : lit ? T.steel : T.muted,
        fontWeight: isActive ? 500 : 400,
        transition: 'color 0.22s ease',
        cursor: 'pointer',
        userSelect: 'none',
      }}>
        {link.label}

        {/* Hairline underline — slides in on active */}
        <span style={{
          position: 'absolute',
          bottom: 2,
          left: 0,
          right: 0,
          height: 1,
          background: isActive
            ? `linear-gradient(90deg, ${T.steel}, transparent 80%)`
            : 'transparent',
          transition: 'background 0.3s ease',
        }} />
      </span>
    </Link>
  );
}

/* ─── Hamburger icon ─── */
function HamburgerIcon({ open }) {
  const base = {
    display: 'block',
    width: 20,
    height: 1.5,
    background: open ? T.steel : T.muted,
    borderRadius: 1,
    transition: 'all 0.28s ease',
    transformOrigin: 'center',
  };
  return (
    <div style={{ width: 20, height: 13, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', cursor: 'pointer' }}>
      <span style={{ ...base, transform: open ? 'translateY(5.75px) rotate(45deg)' : 'none' }} />
      <span style={{ ...base, opacity: open ? 0 : 1, transform: open ? 'scaleX(0)' : 'scaleX(1)' }} />
      <span style={{ ...base, transform: open ? 'translateY(-5.75px) rotate(-45deg)' : 'none' }} />
    </div>
  );
}

/* ─── Main Navbar ─── */
export default function Navbar() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const active = location.pathname === '/' ? 'home' : location.pathname.slice(1);

  useEffect(() => { setIsOpen(false); }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          width: '100%',
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: '1.25rem', sm: '2rem', md: '3rem' },
          background: T.surface,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${scrolled ? 'rgba(26,48,80,0.18)' : T.border}`,
          boxShadow: scrolled
            ? '0 2px 24px rgba(26,48,80,0.07)'
            : 'none',
          transition: 'border-color 0.35s ease, box-shadow 0.35s ease',
          boxSizing: 'border-box',
        }}
      >
        {/* ── Left: wordmark ── */}
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {/* Precision square mark */}
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Outer square */}
            <rect x="1" y="1" width="16" height="16" stroke={T.accent} strokeWidth="1" fill="none" strokeOpacity="0.5" />
            {/* Inner corner marks */}
            <line x1="1" y1="5" x2="4" y2="5" stroke={T.accent} strokeWidth="1" strokeOpacity="0.7" />
            <line x1="5" y1="1" x2="5" y2="4" stroke={T.accent} strokeWidth="1" strokeOpacity="0.7" />
            <line x1="13" y1="1" x2="13" y2="4" stroke={T.accent} strokeWidth="1" strokeOpacity="0.7" />
            <line x1="14" y1="5" x2="17" y2="5" stroke={T.accent} strokeWidth="1" strokeOpacity="0.7" />
            {/* Center dot */}
            <circle cx="9" cy="9" r="1.5" fill={T.steel} fillOpacity="0.7" />
          </svg>

          <span style={{
            fontFamily: T.display,
            fontSize: 16,
            fontWeight: 600,
            color: T.accent,
            letterSpacing: '0.02em',
            lineHeight: 1,
          }}>
            Madhu Vanguri
          </span>
        </Link>

        {/* ── Centre: desktop links ── */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            gap: '2.25rem',
            alignItems: 'center',
          }}
        >
          {links.map(link => (
            <NavItem key={link.key} link={link} isActive={active === link.key} />
          ))}
        </Box>

        {/* ── Right: availability chip + hamburger ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          {/* Availability pill — desktop only */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              borderRadius: '20px',
              border: `1px solid rgba(26,48,80,0.16)`,
              background: 'rgba(255,255,255,0.7)',
            }}
          >
            <span style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: '#2d9e6b',
              flexShrink: 0,
            }} />
            <span style={{
              fontFamily: T.mono,
              fontSize: 9,
              letterSpacing: '0.1em',
              color: '#2d6e50',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}>
              Open to work
            </span>
          </Box>

          {/* Mobile hamburger */}
          <Box
            sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}
            onClick={() => setIsOpen(o => !o)}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setIsOpen(o => !o)}
          >
            <HamburgerIcon open={isOpen} />
          </Box>
        </div>
      </Box>

      {/* ── Mobile backdrop ── */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(26, 48, 80, 0.18)',
            zIndex: 998,
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
          }}
        />
      )}

      {/* ── Mobile dropdown panel ── */}
      <Box
        sx={{ display: { xs: 'block', md: 'none' } }}
        style={{
          position: 'fixed',
          top: 60,
          left: 0,
          right: 0,
          zIndex: 999,
          background: 'rgba(250, 249, 245, 0.97)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${T.borderMd}`,
          transform: isOpen ? 'translateY(0)' : 'translateY(-105%)',
          opacity: isOpen ? 1 : 0,
          transition: 'transform 0.32s cubic-bezier(0.4,0,0.2,1), opacity 0.28s ease',
          pointerEvents: isOpen ? 'all' : 'none',
          overflow: 'hidden',
        }}
      >
        {/* Top hairline accent */}
        <div style={{
          height: 1,
          background: `linear-gradient(90deg, transparent, ${T.steel}, transparent)`,
          opacity: 0.5,
        }} />

        {links.map((link) => (
          <Link
            key={link.key}
            to={link.to}
            onClick={() => { setIsOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            style={{ textDecoration: 'none', display: 'block' }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.5rem',
              borderBottom: `1px solid ${T.border}`,
              background: active === link.key
                ? 'rgba(26, 48, 80, 0.05)'
                : 'transparent',
              transition: 'background 0.2s',
            }}>
              <span style={{
                fontFamily: T.mono,
                fontSize: 11,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: active === link.key ? T.accent : T.muted,
                fontWeight: active === link.key ? 500 : 400,
              }}>
                {link.label}
              </span>

              {/* Active indicator or chevron */}
              {active === link.key ? (
                <span style={{
                  display: 'block',
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background: T.steel,
                }} />
              ) : (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M3.5 2L6.5 5L3.5 8" stroke={T.muted} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </Link>
        ))}

        {/* Bottom strip */}
        <div style={{
          padding: '0.85rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{
            fontFamily: T.mono,
            fontSize: 9,
            color: T.muted,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            opacity: 0.7,
          }}>
            Madhu Vanguri · Portfolio
          </span>
          <span style={{
            fontFamily: T.mono,
            fontSize: 9,
            color: T.muted,
            letterSpacing: '0.1em',
            opacity: 0.7,
          }}>
            {links.length} pages
          </span>
        </div>
      </Box>
    </>
  );
}